import { typeValidator } from '@chilipiper/utils'
import React, { RefObject, useEffect, useRef } from 'react'
import { IconError } from '@chilipiper/icons/figma-generated/src/icon'
import { useTextField } from 'react-aria'
import styled from 'styled-components'
import { layout } from 'styled-system'
import invariant from 'tiny-invariant'
import { Box, ScreenReaderOnly, Text } from '../../../core-components'
import { validatePositioningProps } from '../../../helpers/validatePositioningProps'
import { Icon } from '../../../core-components/icon/Icon'
import { Events } from '../../../types'
import { IconTooltip } from '../../feedback/icon-tooltip/IconTooltip'
import { FocusableHandlerProps, PositioningProps } from '../../types'
import { SettingLabel, SettingLabelContainer } from '../../wayfinding/setting-label/SettingLabel'
import { sharedInputCss, sharedInputContainerCss } from '../helpers'
import { StartContent, StartContentProvider } from '../../data'

type Variant = 'regular' | 'ghost' | 'nested'

type AutoCompleteProps =
  | 'on'
  | 'off'
  | 'tel'
  | 'url'
  | 'email'
  | 'username'
  | 'password'
  | 'given-name'
  | 'family-name'
  | 'organization'
  | 'new-password'
  | 'address-level1'
  | 'current-password'

type Props = {
  autoFocus?: boolean
  'data-id'?: string
  'data-test-id'?: string
  errorMessage?: string
  startContent?: React.ReactNode
  eventsRef?: RefObject<Events<'focus'> | null | undefined>
  isDisabled?: boolean
  isRequired?: boolean
  label: React.ReactNode
  name?: string
  onChange: (value: string) => void
  placeholder: string
  type?: 'text' | 'url' | 'email' | 'password' | 'tel'
  value: string
  variant?: Variant
  autoComplete?: AutoCompleteProps
} & PositioningProps &
  FocusableHandlerProps<HTMLInputElement>

export const Input = ({
  name,
  value,
  onChange,
  onBlur,
  onFocus,
  'data-id': dataId,
  'data-test-id': testId,
  placeholder,
  isRequired,
  errorMessage,
  isDisabled,
  variant = 'regular',
  label,
  type = 'text',
  autoFocus,
  eventsRef,
  autoComplete,
  startContent,
  ...positioningProps
}: Props) => {
  validatePositioningProps(positioningProps, 'Input')
  invariant(
    startContent === undefined || typeValidator(StartContent)(startContent),
    'startContent must be a StartContent component or undefined'
  )

  const ref = useRef<HTMLInputElement>(null)
  const { text, description } = parseLabel(label)
  const { labelProps, inputProps, errorMessageProps, descriptionProps } = useTextField(
    {
      name,
      inputElementType: 'input',
      type,
      placeholder,
      value,
      label: text,
      description: description,
      onChange,
      onBlur: onBlur as React.FocusEventHandler<unknown>,
      onFocus: onFocus as React.FocusEventHandler<unknown>,
      isRequired: isRequired,
      isDisabled: isDisabled,
      errorMessage: errorMessage,
      autoFocus,
    },
    ref
  )

  useEffect(() => {
    if (eventsRef) {
      eventsRef.current = {
        focus: () => {
          setTimeout(() => ref.current?.focus(), 0)
        },
      }
      return () => (eventsRef.current = undefined)
    }
  }, [eventsRef])

  return (
    <SettingLabelContainer
      label={label}
      labelElement='label'
      isRequired={isRequired}
      labelProps={labelProps}
      descriptionProps={descriptionProps}
      {...positioningProps}
    >
      <Box position='relative' width='100%'>
        <StyledInputWrapper>
          <Box position='absolute' top={variant === 'nested' ? 4 : 3} left={5}>
            <StartContentProvider value={{ iconColor: isDisabled ? 'fg/disabled' : 'fg/major' }}>
              {startContent}
            </StartContentProvider>
          </Box>
          <StyledInput
            ref={ref}
            {...inputProps}
            hasError={!!errorMessage}
            variant={variant}
            hasStartContent={Boolean(startContent)}
            data-id={dataId}
            data-test-id={testId}
            autoComplete={autoComplete}
            height={variant === 'nested' ? 9 : 8}
            width='100%'
          />
          {!!errorMessage && variant === 'nested' && (
            <>
              <Box position='absolute' top={3} right={5}>
                <IconTooltip variant='error' content={errorMessage} />
              </Box>
              <ScreenReaderOnly {...errorMessageProps}>{errorMessage}</ScreenReaderOnly>
            </>
          )}
        </StyledInputWrapper>
        {!!errorMessage && variant !== 'nested' && (
          <>
            <StyledIcon icon={IconError} color='fg/caution' position='absolute' top={3} right={5} />
            <Text
              {...errorMessageProps}
              display='block'
              textStyle='body-multiline-tight'
              data-test-id='input-error-message'
              color='fg/caution'
              mt={2}
            >
              {errorMessage}
            </Text>
          </>
        )}
      </Box>
    </SettingLabelContainer>
  )
}

const StyledIcon = styled(Icon)`
  pointer-events: none;
`

const StyledInput = styled.input<{ hasError: boolean; variant: Variant; hasStartContent: boolean }>`
  ${layout}
  padding: 0
    ${({ theme, hasError, variant }) => {
    if (hasError) {
      if (variant === 'nested') {
        return '3.75rem'
      }
      return '3.25rem'
    } else {
      return theme.space[5]
    }
  }}
    0 ${({ theme, hasStartContent }) => (hasStartContent ? theme.space[9] : theme.space[5])};
  text-overflow: ellipsis;
  ${sharedInputCss}
  ${sharedInputContainerCss}
`

const StyledInputWrapper = styled(Box)`
  &:hover ${StyledInput}:not(:disabled) {
    background: ${({ theme }) => theme.colors['bg/form-item-hovered']};
  }

  ${StyledInput}:focus {
    background: ${({ theme }) => theme.colors['bg/form-item-focused']};
  }
`

const parseLabel = (label: React.ReactNode) => {
  invariant(typeValidator(InputLabel)(label), `Input: label must be an Input.Label component`)
  return {
    text: label.props.children,
    description: label.props.description,
  }
}
const InputLabel = (props: React.ComponentProps<typeof SettingLabel>) => <SettingLabel {...props} />
Input.Label = InputLabel
